import { useEffect, useMemo, useState } from "react";

import { AdminButton } from "@/components/admin/button";
import { AdminIcon } from "@/components/admin/icon";
import { Tag } from "@/components/admin/tag";
import { Modal, ModalFooter } from "@/components/ui/modal";
import {
	MultiSelect,
	type MultiSelectOption,
} from "@/components/ui/multi-select";
import { useCategoriesList, useProductsList } from "@/hooks/use-catalog";
import type { Coupon, CouponType } from "@/lib/promotions/promotions-api";
import {
	useCreatePromotion,
	useUpdatePromotion,
} from "@/lib/promotions/promotions-hooks";

interface PromotionModalProps {
	isOpen: boolean;
	onClose: () => void;
	initialData?: Coupon | null;
	onSuccess?: () => void;
}

function InfoTooltip({
	text,
	side = "bottom",
	align = "center",
}: {
	text: string;
	side?: "top" | "bottom";
	align?: "center" | "left" | "right";
}) {
	const alignClasses =
		align === "right"
			? "right-0 translate-x-0"
			: align === "left"
				? "left-0 translate-x-0"
				: "left-1/2 -translate-x-1/2";

	const arrowAlignClasses =
		align === "right"
			? "right-3 translate-x-0"
			: align === "left"
				? "left-3 translate-x-0"
				: "left-1/2 -translate-x-1/2";

	return (
		<span className="group relative inline-flex items-center ml-1.5 cursor-help text-slate-400 hover:text-[var(--a-terracotta)] transition-colors">
			<AdminIcon n="info" s={13} c="#64748b" />
			<span
				className={`pointer-events-none absolute hidden w-64 rounded-md bg-slate-900 p-2.5 text-[11px] font-normal text-slate-100 shadow-2xl group-hover:block z-100 leading-relaxed text-center ${alignClasses} ${
					side === "top" ? "bottom-full mb-2" : "top-full mt-2"
				}`}
			>
				{text}
				<span
					className={`absolute border-4 border-transparent ${arrowAlignClasses} ${
						side === "top"
							? "top-full border-t-slate-900 border-b-transparent"
							: "bottom-full border-b-slate-900 border-t-transparent"
					}`}
				/>
			</span>
		</span>
	);
}

export function PromotionModal({
	isOpen,
	onClose,
	initialData,
	onSuccess,
}: PromotionModalProps) {
	const isEditing = Boolean(initialData);

	const [activeTab, setActiveTab] = useState<"general" | "eligibility">(
		"general",
	);
	const [code, setCode] = useState("");
	const [description, setDescription] = useState("");
	const [type, setType] = useState<CouponType>("PERCENT");
	const [value, setValue] = useState<number | "">("");
	const [minSubtotal, setMinSubtotal] = useState<number | "">("");
	const [maxDiscount, setMaxDiscount] = useState<number | "">("");
	const [isActive, setIsActive] = useState(true);
	const [startsAt, setStartsAt] = useState("");
	const [expiresAt, setExpiresAt] = useState("");
	const [maxRedemptions, setMaxRedemptions] = useState<number | "">("");
	const [oncePerCustomer, setOncePerCustomer] = useState(false);
	const [allowCumulation, setAllowCumulation] = useState(true);
	const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>([]);
	const [selectedProductIds, setSelectedProductIds] = useState<string[]>([]);
	const [errorMessage, setErrorMessage] = useState<string | null>(null);

	const { data: categories = [] } = useCategoriesList({ isActive: true });
	const { data: productsData } = useProductsList({ pageSize: 100 });

	const createMutation = useCreatePromotion();
	const updateMutation = useUpdatePromotion();

	const isPending = createMutation.isPending || updateMutation.isPending;

	// Préparation des options pour le composant MultiSelect
	const categoryOptions: MultiSelectOption[] = useMemo(() => {
		return categories.map((cat) => ({
			value: cat.id,
			label: cat.name,
			badge: cat.slug,
		}));
	}, [categories]);

	const productOptions: MultiSelectOption[] = useMemo(() => {
		const items = productsData?.items || [];
		if (selectedCategoryIds.length === 0) {
			return items.map((p) => ({
				value: p.id,
				label: p.title,
				badge: p.sku,
			}));
		}

		const targetCategoryIds = new Set<string>();
		for (const catId of selectedCategoryIds) {
			targetCategoryIds.add(catId);
			const catObj = categories.find((c) => c.id === catId);
			if (catObj?.children) {
				for (const child of catObj.children) {
					targetCategoryIds.add(child.id);
				}
			}
			for (const c of categories) {
				if (c.parentId === catId) {
					targetCategoryIds.add(c.id);
				}
			}
		}

		const filteredItems = items.filter(
			(p) =>
				targetCategoryIds.has(p.categoryId) ||
				selectedProductIds.includes(p.id),
		);

		return filteredItems.map((p) => ({
			value: p.id,
			label: p.title,
			badge: p.sku,
		}));
	}, [productsData, selectedCategoryIds, selectedProductIds, categories]);

	useEffect(() => {
		if (!isOpen) return;

		if (initialData) {
			setCode(initialData.code);
			setDescription(initialData.description || "");
			setType(initialData.type);
			setValue(initialData.value);
			setMinSubtotal(initialData.minSubtotal || 0);
			setMaxDiscount(initialData.maxDiscount || 0);
			setIsActive(initialData.isActive);
			setStartsAt(
				initialData.startsAt
					? new Date(initialData.startsAt).toISOString().slice(0, 16)
					: "",
			);
			setExpiresAt(
				initialData.expiresAt
					? new Date(initialData.expiresAt).toISOString().slice(0, 16)
					: "",
			);
			setMaxRedemptions(
				initialData.maxRedemptions !== null &&
					initialData.maxRedemptions !== undefined
					? initialData.maxRedemptions
					: "",
			);
			setOncePerCustomer(Boolean(initialData.oncePerCustomer));
			setAllowCumulation(
				initialData.allowCumulation !== undefined
					? Boolean(initialData.allowCumulation)
					: true,
			);
			setSelectedCategoryIds(initialData.categoryIds || []);
			setSelectedProductIds(initialData.productIds || []);
		} else {
			setCode("");
			setDescription("");
			setType("PERCENT");
			setValue("");
			setMinSubtotal("");
			setMaxDiscount("");
			setIsActive(true);
			setStartsAt("");
			setExpiresAt("");
			setMaxRedemptions("");
			setOncePerCustomer(false);
			setAllowCumulation(true);
			setSelectedCategoryIds([]);
			setSelectedProductIds([]);
		}
		setActiveTab("general");
		setErrorMessage(null);
	}, [initialData, isOpen]);

	if (!isOpen) return null;

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setErrorMessage(null);

		const cleanCode = code.trim().toUpperCase();
		if (!cleanCode) {
			setErrorMessage("Le code promo est obligatoire");
			return;
		}

		if (value === "" || Number(value) < 0) {
			setErrorMessage("La valeur de remise doit être un nombre positif");
			return;
		}

		if (type === "PERCENT" && Number(value) > 100) {
			setErrorMessage("Un pourcentage ne peut pas dépasser 100%");
			return;
		}

		if (startsAt && expiresAt && new Date(startsAt) >= new Date(expiresAt)) {
			setErrorMessage(
				"La date de début doit être antérieure à la date d’expiration",
			);
			return;
		}

		const payload = {
			code: cleanCode,
			description: description.trim() || null,
			type,
			value: Number(value),
			minSubtotal: minSubtotal !== "" ? Number(minSubtotal) : 0,
			maxDiscount:
				type === "PERCENT" && maxDiscount !== "" ? Number(maxDiscount) : 0,
			isActive,
			startsAt: startsAt ? new Date(startsAt).toISOString() : null,
			expiresAt: expiresAt ? new Date(expiresAt).toISOString() : null,
			maxRedemptions: maxRedemptions !== "" ? Number(maxRedemptions) : null,
			oncePerCustomer,
			allowCumulation,
			categoryIds: selectedCategoryIds,
			productIds: selectedProductIds,
		};

		try {
			if (isEditing && initialData) {
				await updateMutation.mutateAsync({ id: initialData.id, data: payload });
			} else {
				await createMutation.mutateAsync(payload);
			}
			onSuccess?.();
			onClose();
		} catch (err: unknown) {
			const errorObj = err as {
				response?: { data?: { message?: string | string[] } };
				message?: string;
			};
			const msg =
				errorObj?.response?.data?.message ||
				errorObj?.message ||
				"Une erreur est survenue lors de l’enregistrement";
			setErrorMessage(Array.isArray(msg) ? msg.join(", ") : String(msg));
		}
	};

	// Libellés légèrement plus doux (#475569 / Slate 600) que les valeurs saisies (#0f172a)
	const labelStyle = {
		display: "block",
		fontSize: 12,
		fontWeight: 600,
		color: "#475569",
		marginBottom: 5,
	};

	const inlineLabelStyle = {
		display: "inline-flex",
		alignItems: "center",
		fontSize: 12,
		fontWeight: 600,
		color: "#475569",
		marginBottom: 5,
	};

	return (
		<Modal
			title={
				isEditing ? `Éditer le code ${initialData?.code}` : "Nouveau code promo"
			}
			subtitle={
				isEditing
					? "Modifier les paramètres du code de réduction"
					: "Créer un nouveau code de réduction pour vos clients"
			}
			onClose={onClose}
			width={580}
		>
			<form onSubmit={handleSubmit} className="grid gap-4">
				{errorMessage && (
					<div
						style={{
							padding: "10px 14px",
							borderRadius: 8,
							background: "rgba(220, 38, 38, 0.1)",
							border: "1px solid rgba(220, 38, 38, 0.3)",
							color: "#dc2626",
							fontSize: 12,
							fontWeight: 600,
						}}
					>
						{errorMessage}
					</div>
				)}

				{/* Navigation des Onglets — Douce, compacte et non imposante */}
				<div
					style={{
						display: "grid",
						gridTemplateColumns: "1fr 1fr",
						gap: 3,
						padding: 3,
						background: "var(--a-surface-2)",
						border: "1px solid var(--a-border)",
						borderRadius: 9,
						marginBottom: 16,
						width: "100%",
					}}
				>
					<button
						type="button"
						onClick={() => setActiveTab("general")}
						className="active:scale-[0.96]"
						style={{
							display: "inline-flex",
							alignItems: "center",
							justifyContent: "center",
							gap: 6,
							padding: "6px 12px",
							borderRadius: 6,
							fontSize: 12,
							fontWeight: activeTab === "general" ? 600 : 500,
							cursor: "pointer",
							border:
								activeTab === "general"
									? "1px solid var(--a-border)"
									: "1px solid transparent",
							transition:
								"color 0.15s ease, background-color 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease",
							background:
								activeTab === "general" ? "var(--a-surface)" : "transparent",
							color:
								activeTab === "general" ? "var(--a-ink)" : "var(--a-ink-mute)",
							boxShadow:
								activeTab === "general" ? "var(--a-shadow-sm)" : "none",
						}}
					>
						<AdminIcon
							n="tag"
							s={13}
							c={
								activeTab === "general"
									? "var(--a-terracotta)"
									: "var(--a-ink-mute)"
							}
						/>
						<span>Général & Remise</span>
					</button>

					<button
						type="button"
						onClick={() => setActiveTab("eligibility")}
						className="active:scale-[0.96]"
						style={{
							display: "inline-flex",
							alignItems: "center",
							justifyContent: "center",
							gap: 6,
							padding: "6px 12px",
							borderRadius: 6,
							fontSize: 12,
							fontWeight: activeTab === "eligibility" ? 600 : 500,
							cursor: "pointer",
							border:
								activeTab === "eligibility"
									? "1px solid var(--a-border)"
									: "1px solid transparent",
							transition:
								"color 0.15s ease, background-color 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease",
							background:
								activeTab === "eligibility"
									? "var(--a-surface)"
									: "transparent",
							color:
								activeTab === "eligibility"
									? "var(--a-ink)"
									: "var(--a-ink-mute)",
							boxShadow:
								activeTab === "eligibility" ? "var(--a-shadow-sm)" : "none",
						}}
					>
						<AdminIcon
							n="shield"
							s={13}
							c={
								activeTab === "eligibility"
									? "var(--a-terracotta)"
									: "var(--a-ink-mute)"
							}
						/>
						<span>Éligibilité & Cumul</span>
						{(selectedCategoryIds.length > 0 ? 1 : 0) +
							(selectedProductIds.length > 0 ? 1 : 0) +
							(oncePerCustomer ? 1 : 0) >
							0 && (
							<span
								className="tn"
								style={{
									marginLeft: 4,
									padding: "1px 5px",
									borderRadius: 4,
									background: "var(--a-surface-2)",
									color: "var(--a-ink-soft)",
									fontSize: 10,
									fontWeight: 600,
								}}
							>
								{(selectedCategoryIds.length > 0 ? 1 : 0) +
									(selectedProductIds.length > 0 ? 1 : 0) +
									(oncePerCustomer ? 1 : 0)}
							</span>
						)}
					</button>
				</div>

				{/* Onglet 1 : Informations Générales & Valeur de la Remise */}
				{activeTab === "general" && (
					<div className="grid gap-3.5 animate-in fade-in duration-150">
						{/* Identification du code promo */}
						<div className="grid grid-cols-2 gap-3">
							<div>
								<label htmlFor="promo-code-input" style={labelStyle}>
									Code promo <span style={{ color: "#d97706" }}>*</span>
								</label>
								<input
									id="promo-code-input"
									type="text"
									className="aform-i w-full uppercase font-mono"
									style={{ color: "#0f172a", fontWeight: 700 }}
									placeholder="Ex. SUMMER20"
									value={code}
									onChange={(e) => setCode(e.target.value.toUpperCase())}
									required
								/>
							</div>

							<div>
								<label htmlFor="promo-type-select" style={labelStyle}>
									Type de remise <span style={{ color: "#d97706" }}>*</span>
								</label>
								<select
									id="promo-type-select"
									className="aform-i w-full"
									style={{ color: "#0f172a", fontWeight: 600 }}
									value={type}
									onChange={(e) => setType(e.target.value as CouponType)}
								>
									<option value="PERCENT">Pourcentage (%)</option>
									<option value="FIXED">Montant fixe (XAF)</option>
								</select>
							</div>
						</div>

						<div>
							<label htmlFor="promo-desc-input" style={labelStyle}>
								Description / Libellé
							</label>
							<input
								id="promo-desc-input"
								type="text"
								className="aform-i w-full"
								style={{ color: "#0f172a", fontWeight: 500 }}
								placeholder="Ex. Offre spéciale pour l'été 30%"
								value={description}
								onChange={(e) => setDescription(e.target.value)}
							/>
						</div>

						{/* Valeurs et Limites de réduction */}
						<div className="grid grid-cols-3 gap-3">
							<div>
								<label htmlFor="promo-value-input" style={labelStyle}>
									{type === "PERCENT" ? "Remise (%)" : "Remise (FCFA)"}{" "}
									<span style={{ color: "#d97706" }}>*</span>
								</label>
								<input
									id="promo-value-input"
									type="number"
									className="aform-i w-full"
									style={{ color: "#0f172a", fontWeight: 700 }}
									min={0}
									max={type === "PERCENT" ? 100 : undefined}
									value={value}
									onChange={(e) =>
										setValue(
											e.target.value === "" ? "" : Number(e.target.value),
										)
									}
									required
								/>
							</div>

							<div>
								<label
									htmlFor="promo-min-subtotal-input"
									style={inlineLabelStyle}
								>
									Achat min. (FCFA)
									<InfoTooltip
										text="Sous-total minimum requis dans le panier du client pour pouvoir appliquer ce code promo. Mettez 0 pour aucun minimum."
										side="bottom"
										align="center"
									/>
								</label>
								<input
									id="promo-min-subtotal-input"
									type="number"
									className="aform-i w-full"
									style={{ color: "#0f172a", fontWeight: 600 }}
									min={0}
									value={minSubtotal}
									onChange={(e) =>
										setMinSubtotal(
											e.target.value === "" ? "" : Number(e.target.value),
										)
									}
								/>
							</div>

							{type === "PERCENT" ? (
								<div>
									<label
										htmlFor="promo-max-discount-input"
										style={inlineLabelStyle}
									>
										Plafond (FCFA)
										<InfoTooltip
											text="Montant maximum de réduction accordé en FCFA. Exemple: avec 20% sur 100 000 FCFA et un plafond de 10 000 FCFA, la réduction maximale sera limitée à 10 000 FCFA. Mettez 0 pour aucune limite."
											side="bottom"
											align="right"
										/>
									</label>
									<input
										id="promo-max-discount-input"
										type="number"
										className="aform-i w-full"
										style={{ color: "#0f172a", fontWeight: 600 }}
										min={0}
										placeholder="0 = illimité"
										value={maxDiscount}
										onChange={(e) =>
											setMaxDiscount(
												e.target.value === "" ? "" : Number(e.target.value),
											)
										}
									/>
								</div>
							) : (
								<div>
									<label
										htmlFor="promo-max-redemptions-fixed-input"
										style={inlineLabelStyle}
									>
										Max utilisations
										<InfoTooltip
											text="Nombre total d’utilisations autorisées sur toute la boutique avant que le code ne devienne automatiquement épuisé. Laissez vide pour illimité."
											side="bottom"
											align="right"
										/>
									</label>
									<input
										id="promo-max-redemptions-fixed-input"
										type="number"
										className="aform-i w-full"
										style={{ color: "#0f172a", fontWeight: 600 }}
										min={1}
										placeholder="Illimité"
										value={maxRedemptions}
										onChange={(e) =>
											setMaxRedemptions(
												e.target.value === "" ? "" : Number(e.target.value),
											)
										}
									/>
								</div>
							)}
						</div>

						{type === "PERCENT" && (
							<div>
								<label
									htmlFor="promo-max-redemptions-percent-input"
									style={inlineLabelStyle}
								>
									Limite d’utilisations max globales
									<InfoTooltip
										text="Nombre total d’utilisations autorisées sur toute la boutique par l’ensemble des clients avant que le code ne soit marqué épuisé. Laissez vide pour illimité."
										side="bottom"
										align="left"
									/>
								</label>
								<input
									id="promo-max-redemptions-percent-input"
									type="number"
									className="aform-i w-full"
									style={{ color: "#0f172a", fontWeight: 600 }}
									min={1}
									placeholder="Laisser vide pour illimité"
									value={maxRedemptions}
									onChange={(e) =>
										setMaxRedemptions(
											e.target.value === "" ? "" : Number(e.target.value),
										)
									}
								/>
							</div>
						)}

						{/* Période de validité */}
						<div className="grid grid-cols-2 gap-3">
							<div>
								<label htmlFor="promo-starts-at-input" style={labelStyle}>
									Début de validité
								</label>
								<input
									id="promo-starts-at-input"
									type="datetime-local"
									className="aform-i w-full"
									style={{ color: "#0f172a", fontWeight: 600 }}
									value={startsAt}
									onChange={(e) => setStartsAt(e.target.value)}
								/>
							</div>

							<div>
								<label htmlFor="promo-expires-at-input" style={labelStyle}>
									Fin de validité
								</label>
								<input
									id="promo-expires-at-input"
									type="datetime-local"
									className="aform-i w-full"
									style={{ color: "#0f172a", fontWeight: 600 }}
									value={expiresAt}
									onChange={(e) => setExpiresAt(e.target.value)}
								/>
							</div>
						</div>

						{/* Case à cocher d'activation & Aperçu en direct */}
						<div className="flex items-center gap-2.5 pt-1">
							<input
								type="checkbox"
								id="isActiveCheck"
								checked={isActive}
								onChange={(e) => setIsActive(e.target.checked)}
								style={{ accentColor: "var(--a-terracotta)" }}
								className="w-4 h-4 cursor-pointer rounded"
							/>
							<label
								htmlFor="isActiveCheck"
								style={{
									fontSize: 12,
									fontWeight: 600,
									color: "#475569",
									cursor: "pointer",
								}}
							>
								Activer ce code promo immédiatement
							</label>
						</div>

						{code.trim() && (
							<div
								style={{
									background: "var(--a-terra-soft)",
									border: "1px solid rgba(198, 86, 58, 0.25)",
									borderRadius: 8,
									padding: "10px 14px",
									marginTop: 4,
								}}
								className="flex items-center justify-between animate-in fade-in duration-150"
							>
								<div className="flex items-center gap-2">
									<Tag
										variant="terra"
										noDot
										style={{ fontWeight: 700 }}
										className="font-mono"
									>
										{code.toUpperCase()}
									</Tag>
									<span
										style={{
											fontSize: 12,
											fontWeight: 600,
											color: "var(--a-terra-deep)",
										}}
									>
										{type === "PERCENT"
											? `-${value || 0}% sur la commande`
											: `-${(Number(value) || 0).toLocaleString("fr-FR")} FCFA sur la commande`}
									</span>
								</div>
								<div
									style={{ fontSize: 11, color: "var(--a-ink-mute)" }}
									className="font-mono"
								>
									{minSubtotal
										? `Min. ${Number(minSubtotal).toLocaleString("fr-FR")} F`
										: "Sans min. d'achat"}
								</div>
							</div>
						)}
					</div>
				)}

				{/* Onglet 2 : Conditions d'éligibilité & Règles de cumul */}
				{activeTab === "eligibility" && (
					<div className="grid gap-4 animate-in fade-in duration-150">
						{/* Règles de cumul & utilisation unique */}
						<div className="grid grid-cols-2 gap-3 bg-slate-50 p-3 rounded-lg border border-slate-200/60 hover:border-slate-300 transition-colors">
							<label
								htmlFor="oncePerCustomerCheck"
								className="flex items-start gap-2.5 cursor-pointer"
							>
								<input
									type="checkbox"
									id="oncePerCustomerCheck"
									checked={oncePerCustomer}
									onChange={(e) => setOncePerCustomer(e.target.checked)}
									style={{ accentColor: "var(--a-terracotta)" }}
									className="mt-0.5 w-4 h-4 cursor-pointer rounded"
								/>
								<div>
									<span
										style={{
											fontSize: 12,
											fontWeight: 600,
											color: "#0f172a",
											display: "block",
										}}
									>
										1 seule fois par client
									</span>
									<p className="text-[11px] text-slate-500 leading-tight mt-0.5">
										Bloque la réutilisation du code après la 1re commande du
										client.
									</p>
								</div>
							</label>

							<label
								htmlFor="allowCumulationCheck"
								className="flex items-start gap-2.5 cursor-pointer"
							>
								<input
									type="checkbox"
									id="allowCumulationCheck"
									checked={allowCumulation}
									onChange={(e) => setAllowCumulation(e.target.checked)}
									style={{ accentColor: "var(--a-terracotta)" }}
									className="mt-0.5 w-4 h-4 cursor-pointer rounded"
								/>
								<div>
									<span
										style={{
											fontSize: 12,
											fontWeight: 600,
											color: "#0f172a",
											display: "block",
										}}
									>
										Cumulable avec autres remises
									</span>
									<p className="text-[11px] text-slate-500 leading-tight mt-0.5">
										Décocher pour interdire l'application aux articles déjà
										remisés.
									</p>
								</div>
							</label>
						</div>

						{/* Restriction Catégories */}
						<div>
							<span style={inlineLabelStyle}>
								Catégories éligibles
								<InfoTooltip
									text="Sélectionnez une ou plusieurs catégories. Laissez vide pour autoriser toutes les catégories du catalogue."
									side="top"
									align="left"
								/>
							</span>
							<MultiSelect
								options={categoryOptions}
								selectedValues={selectedCategoryIds}
								onChange={setSelectedCategoryIds}
								placeholder="Toutes les catégories (aucune restriction)"
								searchPlaceholder="Rechercher une catégorie..."
								emptyMessage="Aucune catégorie correspondante"
							/>
						</div>

						{/* Restriction Produits */}
						<div>
							<span style={inlineLabelStyle}>
								Produits spécifiques éligibles (optionnel)
								<InfoTooltip
									text="Sélectionnez un ou meillieurs produits. Laissez vide pour autoriser tous les produits."
									side="top"
									align="left"
								/>
							</span>
							<MultiSelect
								options={productOptions}
								selectedValues={selectedProductIds}
								onChange={setSelectedProductIds}
								placeholder={
									selectedCategoryIds.length > 0
										? "Tous les produits des catégories sélectionnées"
										: "Tous les produits (aucun produit spécifique)"
								}
								searchPlaceholder="Rechercher un produit par titre ou SKU..."
								emptyMessage={
									selectedCategoryIds.length > 0
										? "Aucun produit trouvé dans les catégories sélectionnées"
										: "Aucun produit correspondant"
								}
							/>
						</div>
					</div>
				)}

				<ModalFooter>
					<AdminButton
						variant="ghost"
						type="button"
						onClick={onClose}
						disabled={isPending}
					>
						Annuler
					</AdminButton>
					<AdminButton variant="primary" type="submit" disabled={isPending}>
						{isPending
							? "Enregistrement…"
							: isEditing
								? "Sauvegarder les modifications"
								: "Créer le code promo"}
					</AdminButton>
				</ModalFooter>
			</form>
		</Modal>
	);
}
